import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, KeyRound, Terminal, PlayCircle, X } from "lucide-react";
import { useLang } from "@/lib/i18n";

const CURL_SAMPLE = 'curl -H "X-API-Key: $LOFTPRESS_KEY" \\\n  "https://loftpress.shop/api/v1/currency/latest?base=USD"';

export default function OnboardingCard({ hasApiKey, hasUsage }) {
  const { t } = useLang();
  const [dismissed, setDismissed] = React.useState(
    () => localStorage.getItem("lp_onboarding_done") === "1",
  );
  const [copied, setCopied] = React.useState(false);

  const step1Done = !!hasApiKey;
  // If a user has already made a successful API call, they clearly copied
  // (or wrote) a request themselves — auto-complete step 2 so the card
  // reflects real progress.
  const step2Done = copied || (!!hasApiKey && !!hasUsage);
  const step3Done = !!hasUsage;
  const allDone = step1Done && step2Done && step3Done;

  React.useEffect(() => {
    if (allDone) localStorage.setItem("lp_onboarding_done", "1");
  }, [allDone]);

  if (dismissed || allDone) return null;

  const doCopy = () => {
    navigator.clipboard.writeText(CURL_SAMPLE);
    setCopied(true);
  };
  const dismiss = () => {
    localStorage.setItem("lp_onboarding_done", "1");
    setDismissed(true);
  };

  const Step = ({ done, icon: Icon, title, action }) => (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${done ? "border-emerald-500/25 bg-emerald-500/5" : "border-white/10 bg-white/[0.02]"}`}>
      {done ? <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
            : <Circle className="w-5 h-5 text-white/30 shrink-0 mt-0.5" />}
      <div className="flex-1 min-w-0">
        <div className={`text-sm ${done ? "text-white/60 line-through" : "text-white/90"}`}>
          <Icon className="w-3.5 h-3.5 inline mr-1.5 text-[#d4af37]" />
          {title}
        </div>
        {!done && action && <div className="mt-2">{action}</div>}
      </div>
    </div>
  );

  return (
    <div className="lp-card p-5 mb-6 relative" data-testid="onboarding-card">
      <button onClick={dismiss} aria-label="Dismiss" data-testid="onboarding-dismiss"
        className="absolute top-3 right-3 text-white/40 hover:text-white/80">
        <X className="w-4 h-4" />
      </button>
      <div className="text-xs uppercase tracking-widest text-[#d4af37] font-mono mb-1">{t("onboarding.eyebrow")}</div>
      <h2 className="font-display text-xl text-white/95 mb-4">{t("onboarding.title")}</h2>
      <div className="space-y-2">
        <Step done={step1Done} icon={KeyRound} title={t("onboarding.step1")}
          action={<Link to="/dashboard/api-keys" className="lp-btn-primary text-xs py-1.5 px-3" data-testid="onboarding-step1-cta">{t("onboarding.step1_cta")}</Link>} />
        <Step done={step2Done} icon={Terminal} title={t("onboarding.step2")}
          action={<div>
            <pre className="bg-black/40 border border-white/8 rounded p-2 text-[11px] font-mono text-white/80 overflow-x-auto">{CURL_SAMPLE}</pre>
            <button onClick={doCopy} className="lp-btn-secondary text-xs py-1.5 px-3 mt-2" data-testid="onboarding-step2-cta">
              {copied ? t("onboarding.copied") : t("onboarding.step2_cta")}
            </button>
          </div>} />
        <Step done={step3Done} icon={PlayCircle} title={t("onboarding.step3")}
          action={<Link to="/products/currency" className="lp-btn-secondary text-xs py-1.5 px-3" data-testid="onboarding-step3-cta">{t("onboarding.step3_cta")}</Link>} />
      </div>
    </div>
  );
}
